class ShowroomAppointmentCalendar extends HTMLElement {
  constructor() {
    super();

    this.today = new Date();
    this.currentMonthDate = new Date(
      this.today.getFullYear(),
      this.today.getMonth(),
      1,
    );

    this.selectedDate = null;
    this.slots = [];
    this.slotsByDate = new Map();
  }

  connectedCallback() {
    this.section = this.closest(".showroom-appointment");
    this.formStep = this.section?.querySelector("[data-showroom-form-step]");
    this.modal = this.section?.querySelector("[data-showroom-modal]");
    this.modalCloseButtons = this.section?.querySelectorAll(
      "[data-showroom-modal-close]",
    );
    this.selectedTimeElement = this.section?.querySelector(
      "[data-showroom-selected-time]",
    );
    this.slotsScript = this.section?.querySelector("[data-showroom-slots]");

    this.slots = this.getSlots();
    this.slotsByDate = this.getSlotsByDate();

    this.hideTechnicalFields?.();
    this.bindModalEvents?.();
    this.waitForShopifyFormFields?.();
    this.render();
  }

  getSlots() {
    try {
      const slots = this.slotsScript
        ? JSON.parse(this.slotsScript.textContent)
        : [];

      return slots
        .filter((slot) => {
          return String(slot.status).toLowerCase() === "available";
        })
        .sort((a, b) => {
          return `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`);
        });
    } catch (error) {
      return [];
    }
  }

  getSlotsByDate() {
    const slotsByDate = new Map();

    this.slots.forEach((slot) => {
      if (!slot.id || !slot.date || !slot.time) return;

      const dateSlots = slotsByDate.get(slot.date) || [];

      dateSlots.push(slot);
      slotsByDate.set(slot.date, dateSlots);
    });

    return slotsByDate;
  }

  getStartTime(time) {
    return String(time).split("-")[0].trim();
  }

  formatDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  formatDayTitle(dateKey) {
    const [year, month, day] = dateKey.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  formatReadableDate(dateKey) {
    const [year, month, day] = dateKey.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  }

  getMonthLabel(date) {
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  }

  isPastDate(date) {
    const current = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );

    const todayDate = new Date(
      this.today.getFullYear(),
      this.today.getMonth(),
      this.today.getDate(),
    );

    return current < todayDate;
  }

  getAvailableSlotsForDate(dateKey) {
    return this.slotsByDate.get(dateKey) || [];
  }

  hasAvailableSlots(dateKey) {
    return this.getAvailableSlotsForDate(dateKey).length > 0;
  }

  focusSlotsColumn() {
    const slotsColumn = this.querySelector("[data-showroom-slots-column]");
    const slotsTitle = this.querySelector("[data-showroom-slots-title]");

    if (!slotsColumn) return;

    window.requestAnimationFrame(() => {
      const isMobile = window.matchMedia("(max-width: 749px)").matches;

      if (isMobile) {
        slotsColumn.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      slotsTitle?.focus({ preventScroll: !isMobile });
    });
  }

  handleSlotSelect(slot) {
    const startTime = this.getStartTime(slot.time);
    const timezone = slot.timezone || "Europe/Paris";
    const slotId = String(slot.id).startsWith("gid://")
      ? slot.id
      : `gid://shopify/Metaobject/${slot.id}`;

    const setAppointmentFields = () => {
      this.setFormValue?.("slot_metaobject_id", slotId);
      this.setFormValue?.("selected_date", slot.date);
      this.setFormValue?.("selected_time", startTime);
      this.setFormValue?.("selected_timezone", timezone);
    };

    this.renderSelectedTime({
      date: slot.date,
      time: startTime,
      timezone,
    });

    this.openModal?.();

    setAppointmentFields();
    window.setTimeout(setAppointmentFields, 300);
    window.setTimeout(setAppointmentFields, 800);
  }

  handleAppointmentFormSuccess() {
    window.setTimeout(() => {
      window.location.reload();
    }, 2500);
  }

  render() {
    const year = this.currentMonthDate.getFullYear();
    const month = this.currentMonthDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7;

    const wrapper = document.createElement("div");
    wrapper.className = "showroom-calendar";

    const header = this.renderHeader();

    const body = document.createElement("div");
    body.className = "showroom-calendar__body";

    const monthColumn = this.renderMonthColumn({
      year,
      month,
      lastDay,
      startOffset,
    });

    const slotsColumn = document.createElement("div");
    slotsColumn.className = "showroom-calendar__slots-column";

    if (this.selectedDate) {
      this.renderSlots(slotsColumn, this.selectedDate);
    } else {
      const empty = document.createElement("p");
      empty.className = "showroom-calendar__empty";
      empty.textContent = "Select a date to view available appointment times.";

      slotsColumn.appendChild(empty);
    }

    body.appendChild(monthColumn);
    body.appendChild(slotsColumn);

    wrapper.appendChild(header);
    wrapper.appendChild(body);

    this.innerHTML = "";
    this.appendChild(wrapper);
  }

  renderHeader() {
    const header = document.createElement("div");
    header.className = "showroom-calendar__header";

    const title = document.createElement("p");
    title.className = "showroom-calendar__title";
    title.innerHTML = `
      <span class="showroom-calendar__timezone">Timezone: Europe/Paris</span>
    `;

    header.appendChild(title);

    return header;
  }

  renderMonthColumn({ year, month, lastDay, startOffset }) {
    const monthColumn = document.createElement("div");
    monthColumn.className = "showroom-calendar__month-column";

    const monthNav = this.renderMonthNav({ year, month });
    const weekdays = this.renderWeekdays();
    const grid = this.renderMonthGrid({
      year,
      month,
      lastDay,
      startOffset,
    });

    monthColumn.appendChild(monthNav);
    monthColumn.appendChild(weekdays);
    monthColumn.appendChild(grid);

    return monthColumn;
  }

  renderMonthNav({ year, month }) {
    const monthNav = document.createElement("div");
    monthNav.className = "showroom-calendar__month-nav";

    const previousButton = document.createElement("button");
    previousButton.type = "button";
    previousButton.className =
      "showroom-calendar__nav showroom-calendar__nav--previous";
    previousButton.setAttribute("aria-label", "Previous month");
    previousButton.textContent = "‹";

    const isCurrentMonth =
      this.currentMonthDate.getFullYear() === this.today.getFullYear() &&
      this.currentMonthDate.getMonth() === this.today.getMonth();

    if (isCurrentMonth) {
      previousButton.disabled = true;
      previousButton.classList.add("is-disabled");
    } else {
      previousButton.addEventListener("click", () => {
        this.currentMonthDate = new Date(year, month - 1, 1);
        this.selectedDate = null;
        this.render();
      });
    }

    const monthLabel = document.createElement("p");
    monthLabel.className = "showroom-calendar__month-label";
    monthLabel.textContent = this.getMonthLabel(this.currentMonthDate);

    const nextButton = document.createElement("button");
    nextButton.type = "button";
    nextButton.className =
      "showroom-calendar__nav showroom-calendar__nav--next";
    nextButton.setAttribute("aria-label", "Next month");
    nextButton.textContent = "›";

    nextButton.addEventListener("click", () => {
      this.currentMonthDate = new Date(year, month + 1, 1);
      this.selectedDate = null;
      this.render();
    });

    monthNav.appendChild(previousButton);
    monthNav.appendChild(monthLabel);
    monthNav.appendChild(nextButton);

    return monthNav;
  }

  renderWeekdays() {
    const weekdays = document.createElement("div");
    weekdays.className = "showroom-calendar__weekdays";

    ["MO", "TU", "WE", "TH", "FR", "SA", "SU"].forEach((day) => {
      const dayLabel = document.createElement("span");
      dayLabel.textContent = day;

      weekdays.appendChild(dayLabel);
    });

    return weekdays;
  }

  renderMonthGrid({ year, month, lastDay, startOffset }) {
    const grid = document.createElement("div");
    grid.className = "showroom-calendar__grid";

    for (let index = 0; index < startOffset; index += 1) {
      const empty = document.createElement("span");
      empty.className = "showroom-calendar__day showroom-calendar__day--empty";

      grid.appendChild(empty);
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      const date = new Date(year, month, day);
      const dateKey = this.formatDateKey(date);

      const disabled =
        this.isPastDate(date) || !this.hasAvailableSlots(dateKey);

      const dayButton = document.createElement("button");
      dayButton.type = "button";
      dayButton.className = "showroom-calendar__day";
      dayButton.textContent = day;
      dayButton.dataset.date = dateKey;

      if (this.selectedDate === dateKey) {
        dayButton.classList.add("is-selected");
      }

      if (disabled) {
        dayButton.disabled = true;
        dayButton.classList.add("is-disabled");
      } else {
        dayButton.addEventListener("click", () => {
          this.selectedDate = dateKey;
          this.render();
          this.focusSlotsColumn();
        });
      }

      grid.appendChild(dayButton);
    }

    return grid;
  }

  renderSlots(container, dateKey) {
    const slots = this.getAvailableSlotsForDate(dateKey);

    container.innerHTML = "";
    container.dataset.showroomSlotsColumn = "";

    const eyebrow = document.createElement("p");
    eyebrow.className = "showroom-calendar__slots-eyebrow";
    eyebrow.textContent = "Selected date";

    const title = document.createElement("p");
    title.className = "showroom-calendar__selected-date";
    title.textContent = this.formatReadableDate(dateKey);
    title.tabIndex = -1;
    title.dataset.showroomSlotsTitle = "";

    const timezone = document.createElement("p");
    timezone.className = "showroom-calendar__slots-timezone";
    timezone.textContent = "Available times shown in Europe/Paris";

    container.appendChild(eyebrow);
    container.appendChild(title);
    container.appendChild(timezone);

    if (!slots.length) {
      const empty = document.createElement("p");
      empty.className = "showroom-calendar__empty";
      empty.textContent = "No available times for this date.";

      container.appendChild(empty);

      return;
    }

    const slotsGrid = document.createElement("div");
    slotsGrid.className = "showroom-calendar__slots";

    slots.forEach((slot) => {
      const startTime = this.getStartTime(slot.time);
      const slotButton = document.createElement("button");

      slotButton.type = "button";
      slotButton.className = "showroom-calendar__slot";
      slotButton.textContent = startTime;
      slotButton.dataset.date = slot.date;
      slotButton.dataset.time = startTime;
      slotButton.dataset.slotId = slot.id;

      slotButton.addEventListener("click", () => {
        this.handleSlotSelect(slot);
      });

      slotsGrid.appendChild(slotButton);
    });

    container.appendChild(slotsGrid);
  }

  formatSelectedTimeLabel(date, time) {
    const dateLabel = this.formatDayTitle(date);

    return `${dateLabel} at ${time}`;
  }

  renderSelectedTime({ date, time, timezone = "Europe/Paris" }) {
    if (!this.selectedTimeElement) return;

    this.selectedTimeElement.innerHTML = `
      <h3 class="showroom-appointment__selected-time-title">Selected Time</h3>
      <p class="showroom-appointment__selected-time-value">
        ${this.formatSelectedTimeLabel(date, time)} ${timezone}
      </p>
    `;
  }
}

Object.assign(
  ShowroomAppointmentCalendar.prototype,
  window.ShowroomAppointmentModal,
  window.ShowroomAppointmentForm,
);

if (!customElements.get("showroom-appointment-calendar")) {
  customElements.define(
    "showroom-appointment-calendar",
    ShowroomAppointmentCalendar,
  );
}
